import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, 
  Users, 
  Mail,
  Phone,
  Briefcase,
  Search,
  Filter,
  MoreHorizontal,
  Upload,
  FileText,
  Trash2,
  Edit,
  Clock,
  CheckCircle2,
  XCircle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Candidates() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterJob, setFilterJob] = useState(searchParams.get('job_id') || 'all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    job_id: '',
    cv_text: ''
  });
  const [cvFile, setCvFile] = useState(null);

  const handleCvFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez PDF ou Word.");
      return;
    }
    
    setCvFile(file);
    
    // Extract text from file
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/extract-document`, {
        method: 'POST',
        body: formDataUpload,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, cv_text: data.text || '' }));
        toast.success("CV importé avec succès");
      } else {
        toast.error("Erreur lors de l'extraction du CV");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de l'upload");
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterJob]);

  const fetchData = async () => {
    try {
      const [userRes, jobsRes] = await Promise.all([
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API}/api/jobs`, { credentials: 'include' })
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());

      const candidatesUrl = filterJob && filterJob !== 'all'
        ? `${API}/api/candidates?job_id=${filterJob}`
        : `${API}/api/candidates`;
      
      const candidatesRes = await fetch(candidatesUrl, { credentials: 'include' });
      if (candidatesRes.ok) setCandidates(await candidatesRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const [editingCandidate, setEditingCandidate] = useState(null);

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      name: candidate.name,
      email: candidate.email,
      phone: candidate.phone || '',
      job_id: candidate.job_id,
      cv_text: candidate.cv_text || ''
    });
    setDialogOpen(true);
  };

  const deleteCandidate = async (candidateId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce candidat ?")) return;
    
    try {
      const response = await fetch(`${API}/api/candidates/${candidateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Candidat supprimé");
        fetchData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCandidate 
        ? `${API}/api/candidates/${editingCandidate.candidate_id}`
        : `${API}/api/candidates`;
        
      const method = editingCandidate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCandidate ? "Candidat modifié" : "Candidat ajouté");
        setDialogOpen(false);
        setEditingCandidate(null);
        setFormData({ name: '', email: '', phone: '', job_id: '', cv_text: '' });
        fetchData();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const updateStatus = async (candidateId, newStatus) => {
    try {
      const response = await fetch(`${API}/api/candidates/${candidateId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Statut mis à jour");
        fetchData();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteAllCandidates = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer TOUS les candidats ? Cette action est irréversible.")) return;
    
    try {
      let url = `${API}/api/candidates`;
      if (filterJob && filterJob !== 'all') {
        url += `?job_id=${filterJob}`;
      }
      
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Tous les candidats ont été supprimés");
        fetchData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredCandidates = candidates.filter(c => {
    const q = searchTerm.toLowerCase();
    const nameParts = (c.name || "").toLowerCase().split(/\s+/);
    return !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || nameParts.some(p => p.startsWith(q));
  });

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.job_id === jobId);
    return job?.title || 'N/A';
  };

  const getScoreColor = (score) => {
    if (!score) return '';
    if (score >= 75) return 'text-functional-success';
    if (score >= 50) return 'text-functional-warning';
    return 'text-functional-error';
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-ink dark:text-zinc-100">{t('candidates.title')}</h1>
            <p className="text-ink-muted mt-1">{t('candidates.subtitle')}</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {candidates.length > 0 && (
              <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={deleteAllCandidates}>
                <Trash2 className="w-4 h-4 mr-2" />
                Tout supprimer
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingCandidate(null);
              setFormData({ name: '', email: '', phone: '', job_id: '', cv_text: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-candidate-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('candidates.addCandidate')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingCandidate ? "Modifier le candidat" : t('candidates.addCandidate')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('candidates.form.name')} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('candidates.form.namePlaceholder')}
                    required
                    data-testid="candidate-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">{t('candidates.form.email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('candidates.form.emailPlaceholder')}
                    required
                    data-testid="candidate-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('candidates.form.phone')}</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder={t('candidates.form.phonePlaceholder')}
                    data-testid="candidate-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job">{t('candidates.form.selectJob')} *</Label>
                  <Select 
                    value={formData.job_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, job_id: value }))}
                    required
                  >
                    <SelectTrigger data-testid="candidate-job-select">
                      <SelectValue placeholder={t('candidates.form.selectJob')} />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map(job => (
                        <SelectItem key={job.job_id} value={job.job_id}>
                          {job.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cv">{t('candidates.form.cvText')}</Label>
                  <div className="flex gap-2 mb-2">
                    <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleCvFileUpload}
                        className="hidden"
                      />
                      <Upload className="w-4 h-4 mr-2 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {cvFile ? cvFile.name : t('jobDetail.candidateForm.importCv')}
                      </span>
                    </label>
                  </div>
                  <textarea
                    id="cv"
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={formData.cv_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, cv_text: e.target.value }))}
                    placeholder={t('candidates.form.cvPlaceholder')}
                    data-testid="candidate-cv-input"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="candidate-submit-btn">
                    {editingCandidate ? t('common.save') : t('common.add')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <Input
              placeholder={t('common.search') + "..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-candidates-input"
            />
          </div>
          
          <Select value={filterJob} onValueChange={setFilterJob}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-job-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder={t('reports.filterByJob')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('reports.allJobs')}</SelectItem>
              {jobs.map(job => (
                <SelectItem key={job.job_id} value={job.job_id}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Candidates List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-sm" />
            ))}
          </div>
        ) : filteredCandidates.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-ink-muted mx-auto mb-4" />
              <h3 className="font-display font-semibold text-xl text-ink mb-2">
                {t('candidates.noCandidates')}
              </h3>
              <p className="text-ink-muted mb-6">
                {searchTerm ? t('errors.notFound') : t('candidates.noCandidatesDesc')}
              </p>
              {!searchTerm && (
                <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('candidates.addCandidate')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('candidates.table.name')}</TableHead>
                  <TableHead>{t('candidates.table.job')}</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>{t('candidates.table.score')}</TableHead>
                  <TableHead>{t('candidates.table.status')}</TableHead>
                  <TableHead className="text-right">{t('candidates.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <TableRow key={candidate.candidate_id} className="table-row-hover" data-testid={`candidate-row-${candidate.candidate_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-secondary rounded-full flex items-center justify-center text-white font-medium">
                          {candidate.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{candidate.name}</p>
                          <p className="text-xs text-ink-muted">
                            {new Date(candidate.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-ink-muted" />
                        <span className="text-sm">{getJobTitle(candidate.job_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-ink-muted">
                          <Mail className="w-3 h-3" />
                          {candidate.email}
                        </div>
                        {candidate.phone && (
                          <div className="flex items-center gap-2 text-sm text-ink-muted">
                            <Phone className="w-3 h-3" />
                            {candidate.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {candidate.interview_score ? (
                        <span className={`font-mono-score font-bold text-lg ${getScoreColor(candidate.interview_score)}`}>
                          {Math.round(candidate.interview_score)}
                        </span>
                      ) : (
                        <span className="text-ink-muted text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {candidate.status === 'passed' && candidate.overall_score ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          candidate.overall_score >= 70 ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/30' :
                          candidate.overall_score >= 50 ? 'bg-yellow-500/15 text-yellow-600 border border-yellow-500/30' :
                          'bg-red-500/15 text-red-500 border border-red-500/30'
                        }`}>
                          {t(`candidates.status.${candidate.status}`)} ({candidate.overall_score}%)
                        </span>
                      ) : (
                        <span className={`status-badge status-${candidate.status}`}>
                          {t(`candidates.status.${candidate.status}`)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`candidate-actions-${candidate.candidate_id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52 bg-zinc-900 border-zinc-700 text-white">
                          <DropdownMenuItem onClick={() => handleEdit(candidate)} className="gap-2 cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800">
                            <Edit className="w-4 h-4 text-blue-400" />
                            <span>{t('common.edit')}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(candidate.candidate_id, 'interview_scheduled')} className="gap-2 cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800">
                            <Clock className="w-4 h-4 text-amber-400" />
                            <span>{t('candidates.status.interview_scheduled')}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(candidate.candidate_id, 'passed')} className="gap-2 cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <span>{t('candidates.status.passed')}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(candidate.candidate_id, 'rejected')} className="gap-2 cursor-pointer text-orange-400 hover:bg-zinc-800 focus:bg-zinc-800 focus:text-orange-400">
                            <XCircle className="w-4 h-4" />
                            <span>{t('candidates.status.rejected')}</span>
                          </DropdownMenuItem>
                          <div className="border-t border-zinc-700 my-1" />
                          <DropdownMenuItem onClick={() => deleteCandidate(candidate.candidate_id)} className="gap-2 cursor-pointer text-red-400 hover:bg-zinc-800 focus:bg-zinc-800 focus:text-red-400">
                            <Trash2 className="w-4 h-4" />
                            <span>{t('common.delete')}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
